import React from 'react';
import { useIsPrimaryUser, useIsReadOnly } from '../../hooks/usePermissions';
import './permissions.scss';

/**
 * Method 2: Universal Disable Component
 * Shows the feature but makes it unusable for secondary users
 * Automatically detects what feature to check based on props or content
 */

interface DisableForSecondaryUsersProps {
  children: React.ReactElement;
  disabledMessage?: string;
  disabledClassName?: string;
}

export const DisableForSecondaryUsers: React.FC<DisableForSecondaryUsersProps> = ({
  children,
  disabledMessage = "Only authorized users can access this feature",
  disabledClassName = "disabled-feature"
}) => {
  const isPrimaryUser = useIsPrimaryUser();
  const isReadOnly = useIsReadOnly();
  
  // Simplified logic: Block only if user is NOT primary AND has read-only access
  // - isPrimaryUser = true → ALL ACCESS
  // - isPrimaryUser = false + isReadOnly = false → ALL ACCESS  
  // - isPrimaryUser = false + isReadOnly = true → NO ACCESS
  const hasPermission = isPrimaryUser || (!isPrimaryUser && !isReadOnly);

  // Clone the child element and add disabled props
  const disabledChild = React.cloneElement(children, {
    disabled: !hasPermission,
    className: `${children.props.className || ''} ${!hasPermission ? disabledClassName : ''}`.trim(),
    title: !hasPermission ? disabledMessage : children.props.title,
    onClick: hasPermission ? children.props.onClick : undefined,
    onSubmit: hasPermission ? children.props.onSubmit : undefined,
    style: {
      ...children.props.style,
      ...(!hasPermission && {
        opacity: 0.5,
        cursor: 'not-allowed',
        pointerEvents: 'none'
      })
    }
  });

  return disabledChild;
};

/**
 * Method 3: Universal Route Protection Component
 * Blocks access to entire routes/pages for secondary users
 */

interface ProtectFromSecondaryUsersProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
  accessDeniedMessage?: string;
}

export const ProtectFromSecondaryUsers: React.FC<ProtectFromSecondaryUsersProps> = ({
  children,
  fallback,
  redirectTo,
  accessDeniedMessage
}) => {
  const isPrimaryUser = useIsPrimaryUser();
  const isReadOnly = useIsReadOnly();
  
  // Simplified logic: Block only if user is NOT primary AND has read-only access
  // - isPrimaryUser = true → ALL ACCESS
  // - isPrimaryUser = false + isReadOnly = false → ALL ACCESS  
  // - isPrimaryUser = false + isReadOnly = true → NO ACCESS
  const hasPermission = isPrimaryUser || (!isPrimaryUser && !isReadOnly);
  
  // Debug logging (remove this after testing)
  if (window.location.pathname.includes('material-selection')) {
    console.log('🔍 ProtectFromSecondaryUsers Debug:', {
      isPrimaryUser,
      isReadOnly,
      hasPermission,
      pathname: window.location.pathname
    });
  }

  if (!hasPermission) {
    if (redirectTo) {
      // You can implement redirect logic here if needed
      window.location.href = redirectTo;
      return null;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    // Default access denied page
    return <AccessDeniedPage customMessage={accessDeniedMessage} />;
  }

  return <>{children}</>;
};

/**
 * Default Access Denied Page Component - Styled to match app design
 */
const AccessDeniedPage: React.FC<{ 
  customMessage?: string;
}> = ({ customMessage }) => {
  const isPrimaryUser = useIsPrimaryUser();
  const isReadOnly = useIsReadOnly();

  const defaultMessage = "You don't have permission to access this feature.";
  const displayMessage = customMessage || defaultMessage;

  return (
    <div className="access-denied-container">
      <div className="access-denied-modal">
        <div className="access-denied-content">
          <div className="access-denied-header">
            <div className="access-denied-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" 
                  fill="#637381"
                />
              </svg>
            </div>
            <h2 className="access-denied-title">Access Restricted</h2>
          </div>
          
          <div className="access-denied-body">
            <p className="access-denied-message">
              {displayMessage}
            </p>
            
            <p className="access-denied-help">
              {isPrimaryUser 
                ? "There seems to be an issue with your permissions. Please try refreshing the page."
                : isReadOnly
                  ? "Your account has read-only access. Contact your primary account holder to upgrade your permissions."
                  : "Contact your primary account holder for assistance."
              }
            </p>
          </div>
          
          {/* <div className="access-denied-footer">
            <button 
              className="btn-back"
              onClick={() => window.history.back()}
            >
              Back
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};
